import { useEffect, useState } from 'react';

import type {
  DealMineTeam,
  DealStatusChip,
  EditHistoryEntry,
  FieldProposal,
  FieldStatus,
  ProposalDriftConflict,
  ResolvedFieldState,
} from '../../types';
import { colors } from '../../styles/theme';
import { utcIsoToWallClockInTz } from '../../utils/timezone';

/**
 * Shared types, constants, and value helpers for the DealsPage cluster.
 * Extracted verbatim from DealsPage.tsx (D4 componentization, zero behavior
 * change) so the sibling components can import them without a cross-import web.
 */

/**
 * The grid row model. A `FieldProposal` plus the resolved `status` and the
 * dismissed/push_failed context the backend resolver provides (field-UX-
 * refinements spec §10). Rendering keys off `status` rather than re-inferring
 * from prior/proposed/decision.
 */
export type RenderField = FieldProposal & {
  status: FieldStatus;
  write_eligible?: boolean;
  dismissed_proposed_value?: unknown;
  effective_value?: unknown;
  push_error?: string | null;
  dismiss_reason?: string | null;
  edit_history?: EditHistoryEntry[];
};

/** Map the backend resolver row to the grid render model. */
export function resolvedToRenderField(fs: ResolvedFieldState): RenderField {
  return {
    field_name: fs.field_name,
    proposed_value: fs.proposed_value,
    prior_value: fs.prior_value,
    decision: fs.decision,
    confidence: fs.confidence,
    reason: fs.reason,
    evidence_event_ids: fs.evidence_event_ids,
    user_set: fs.status === 'edited' || fs.status === 'accepted',
    materiality: fs.materiality,
    status: fs.status,
    write_eligible: fs.write_eligible,
    dismissed_proposed_value: fs.dismissed_proposed_value,
    effective_value: fs.effective_value,
    push_error: fs.push_error,
    dismiss_reason: fs.dismiss_reason,
    edit_history: fs.edit_history ?? [],
  };
}

/** Fallback for older backends / stale bundles with no field_states: derive a
 *  best-effort status from the legacy FieldProposal (dismissed rows are
 *  already absent from `fields`, so they cannot appear here). */
export function proposalToRenderField(f: FieldProposal): RenderField {
  let status: FieldStatus = 'unchanged';
  if (f.user_set) status = 'edited';
  else if (f.decision === 'propose_update' || f.decision === 'new_value') status = 'proposed';
  return {
    ...f, status, push_error: null,
    write_eligible: status === 'proposed' || status === 'edited',
  };
}

// Slice J §1 — group/sort/user-filter options.
export type GroupBy = 'none' | 'last_event' | 'stage' | 'deal_lead' | 'badge' | 'user';
export type SortBy = 'recent' | 'oldest' | 'pending';

export const GROUP_OPTIONS: { key: GroupBy; label: string }[] = [
  { key: 'none', label: 'None' },
  { key: 'last_event', label: 'Last event' },
  { key: 'stage', label: 'Stage' },
  { key: 'deal_lead', label: 'Deal lead' },
  { key: 'badge', label: 'New vs Update' },
  { key: 'user', label: 'User' },
];

export const SORT_OPTIONS: { key: SortBy; label: string }[] = [
  { key: 'recent', label: 'Most recent' },
  { key: 'oldest', label: 'Oldest' },
  { key: 'pending', label: 'Most pending' },
];

export const LS_GROUP = 'deals.groupBy';
export const LS_SORT = 'deals.sortBy';

// B-2c scaffold + B-2d interactivity:
//   - Status chip filters (Pending / Pushed / Dismissed / All)
//   - Mine / Team / All toggle
//   - Search box
//   - Per-deal Dismiss with structured reason modal
//   - Per-field Accept (lock as user-set), Edit (override + capture diff),
//     Dismiss (with field-level reason taxonomy)
//   - Editable Custom Instructions
//   - Editable Proposed Affinity Note
//
// Affinity write-back (Log Deal / Push Updates / Log Pass) lands in B-2f.
// Cross-tab Pipeline-row pending flag + mobile responsive lands in B-2e.

export const CHIPS: { key: DealStatusChip; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'pushed', label: 'Pushed' },
  { key: 'dismissed', label: 'Dismissed' },
  { key: 'all', label: 'All' },
];

// Cross-team scopes are senior/admin only. The backend enforces the same
// rule for mine_team=team/all; this just keeps the visible controls honest.
export const MINE_TEAM_CROSS_TEAM: { key: DealMineTeam; label: string }[] = [
  { key: 'mine', label: 'Mine' },
  { key: 'team', label: 'Team' },
  { key: 'all', label: 'All' },
];

export const MINE_TEAM_SELF_ONLY: { key: DealMineTeam; label: string }[] = [
  { key: 'mine', label: 'Mine' },
];

const dealUpdateBadgeStyle = {
  background: colors.badge.pendingUpdate.bg,
  color: colors.badge.pendingUpdate.color,
  border: `1px solid ${colors.badge.pendingUpdate.border}`,
};

const newDealBadgeStyle = {
  background: colors.badge.newDeal.bg,
  color: colors.badge.newDeal.color,
  border: `1px solid ${colors.badge.newDeal.border}`,
};

export function badgeStyleFor(badge: string) {
  return badge === 'NEW_DEAL' ? newDealBadgeStyle : dealUpdateBadgeStyle;
}

export function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

// Wave A #24b — narrow type guard for the HTTP 409 detail payload
// emitted by the BE when pre-write drift fires. Keeps `unknown` from
// ApiError.detail typed as the expected shape before render.
export function isDriftDetail(detail: unknown): detail is {
  error: string;
  message?: string;
  conflicts: ProposalDriftConflict[];
} {
  if (!detail || typeof detail !== 'object') return false;
  const d = detail as Record<string, unknown>;
  return (
    d.error === 'affinity_drift' &&
    Array.isArray(d.conflicts)
  );
}

export function stringValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

export function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return v.map((x) => formatValue(x)).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// Cheap "first.last@ember-infra.com" → "First L." formatter for chips.
// Keeps the team row compact; full email shows as the chip's title attr
// via the parent element if needed.
export function emailToShortName(email: string): string {
  const local = email.split('@')[0] ?? email;
  const parts = local.split(/[._]/).filter(Boolean);
  if (parts.length === 0) return email;
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  if (parts.length === 1) return cap(parts[0]);
  return `${cap(parts[0])} ${parts[1].charAt(0).toUpperCase()}.`;
}

export const _shortName = emailToShortName;

export function coerceValue(s: string): unknown {
  const trimmed = s.trim();
  if (trimmed === '') return null;
  // Try numeric (integer or float) first; otherwise keep as string.
  if (/^-?\d+$/.test(trimmed)) return parseInt(trimmed, 10);
  if (/^-?\d*\.\d+$/.test(trimmed)) return parseFloat(trimmed);
  return s;
}

// Timestamps render in Eastern Time everywhere on this tab. The team is
// distributed but the deals are reviewed during US business hours and
// the existing Pipeline + Agenda tabs already standardize on ET — same
// convention here. Short form drops year/seconds for list items; full
// form keeps the timezone abbrev so it's unambiguous.
const _ET = 'America/New_York';

export function formatEtShort(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: _ET,
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function formatEtFull(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: _ET,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
}

/**
 * HH:MM (24h, zero-padded) in the VIEWER's local timezone — deliberately not
 * ET, unlike every other timestamp on this tab. Used only for the Phase B
 * pending chip's `queued_at` (queue-only Phase B, owner ruling 2026-08-20),
 * which an operator reads as "how long has this been sitting."
 *
 * Reuses `utcIsoToWallClockInTz` (built for the Event Details editor) rather
 * than a new parser: `queued_at` comes off the backend's `_iso()` helper,
 * which is a bare `isoformat()` on a naive `datetime.utcnow()` — no trailing
 * `Z` — so a plain `new Date(iso)` would parse it as local time and double-
 * apply the offset. `utcIsoToWallClockInTz` already normalizes that.
 *
 * Returns '' for null/unparseable input; the caller falls back to a plain
 * label rather than showing a blank time.
 */
export function formatLocalHHMM(iso: string | null): string {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const wallClock = utcIsoToWallClockInTz(iso, tz);
  return wallClock ? wallClock.slice(11, 16) : '';
}
